import { useQuery } from '@urql/vue';
import { computed } from 'vue';

import { useLessonShareCode } from '~/composables/unified/useLessonShareCode';
import { LESSON_SHARED_BY_CODE } from '~/graphql/documents/lessonStart';
import { UNIFIED_ONBOARDING_BLINKY_MESSAGE } from '~/graphql/documents/unified';
import { parseSelectedEntityFromQuery } from '~/utils/unified/selectedEntity';
import { readUtmKeyFromQuery } from '~/utils/unified/signupLink';

export const usePersonalisedOnboarding = () => {
  const route = useRoute();
  const { t } = useI18n();
  const selectedEntity = parseSelectedEntityFromQuery(route.query);
  const utmKey = readUtmKeyFromQuery(route.query) || null;
  const { shareCode } = useLessonShareCode();

  const { data: blinkyData, fetching: blinkyFetching } = useQuery({
    query: UNIFIED_ONBOARDING_BLINKY_MESSAGE,
    variables: { selectedEntity: selectedEntity ?? null, utmKey },
    pause: computed(() => shareCode.value !== null),
  });

  const { data: shareData, fetching: shareFetching } = useQuery({
    query: LESSON_SHARED_BY_CODE,
    variables: computed(() => ({ code: shareCode.value ?? '' })),
    pause: computed(() => shareCode.value === null),
  });

  const message = computed(() => {
    if (shareCode.value) {
      const share = shareData.value?.lessonSharedByCode;
      const teacher = share?.teacherDisplayName ?? t('unified.lessonStart.fallbackTeacher');
      const lesson = share?.title;
      if (!lesson) {
        return null;
      }
      return t('unified.lessonStart.blinkyIntro', { teacher, lesson });
    }
    return blinkyData.value?.unifiedOnboardingBlinkyMessage?.message ?? null;
  });
  const exitMessage = computed(() => blinkyData.value?.unifiedOnboardingBlinkyMessage?.exitMessage ?? null);
  // "back to the campaign" is only true of a student who arrived on one, and a campaign is what the
  // utm key names. A selected entity is not one: it rides every save-progress bounce out of the app
  // and back, so reading it as a campaign promises a ghost arrival a campaign it never saw. Everyone
  // without a utm key gets the neutral copy.
  const arrivedOnCampaign = !!utmKey;

  const completeMessage = computed(() => {
    if (shareCode.value) {
      return t('unified.lessonStart.completeMessage');
    }
    return exitMessage.value
      ?? t(arrivedOnCampaign ? 'unified.onboarding.completeCampaign' : 'unified.onboarding.complete');
  });
  const fetching = computed(() => blinkyFetching.value || shareFetching.value);

  return { message, completeMessage, fetching, utmKey };
};
